import { Injectable } from '@angular/core';
import { decimal2, IsInvoicePublished, NumberUtil, ValidInvoicePaymentsExists } from 'src/app/shared/utils/util-function';
import * as _ from 'lodash';
import { InvoiceEntityDTO, InvoiceEstimatedLineItemsDTO } from 'src/app/shared/invoice/invoice.interface';
import { EstimateDetailsEntityDTO, EstimateDetailsSectionEntityDTO, EstimateEntityDTO, EstimateSectionDisplayRenderType } from 'src/app/shared/modals/estimate.modal';
import { EstimateDetailSectionCostlineTypeEnum, EstimateDetailTypeEnum } from 'src/app/shared/enums/estimate/estimate.enum';
import { cloneDeep, orderBy, sum } from 'lodash';
import { EstimatesService } from '../estimate-service/estimate.service';
import { IPayment } from 'src/app/shared/interfaces/payment/payment.interface';
import { PaymentStatusTypes } from 'src/app/shared/enums/payment/payment.enum';
import { DiscountService } from '../discount.service';
import { InvoiceStatusEnum } from 'src/app/shared/enums/invoice/invoice.enum';
import { MomentHelper } from 'src/app/shared/utils/moment-helper';
import { estimateTaxMode, invoiceTaxesPerLine } from 'src/app/shared/utils/estimate-tax-mode';
import { textToHtml } from 'src/app/shared/utils/html-text';
@Injectable({
  providedIn: 'root'
})
export class InvoiceService {

  constructor(private estimateService: EstimatesService, private discountService: DiscountService) { }


  calculateInvoiceTotalValues(invoice: InvoiceEntityDTO) {
    invoice.SubTotal = 0;
    invoice.TaxableSubTotal = 0;
    invoice.Total = 0;
    invoice.RemainingBalance = 0;
    invoice.PaymentToDate = 0;
    invoice.CalculatedTax = 0;
    invoice.EstimatedLineItemsSubTotal = 0;
    invoice.EstimatedLineItemsTotal = 0;
    invoice.EstimatedLineItemsTotalPercent = 0;

    invoice = this.renderInvoiceDiscounts(invoice);
    const taxesPerLine = invoiceTaxesPerLine(invoice);

    if (invoice.AdditionalLineItems?.length) {
      invoice.SubTotal = _.sum(invoice.AdditionalLineItems.map(x => NumberUtil.ToNumber(x.Price)));
      invoice.TaxableSubTotal = _.sum(invoice.AdditionalLineItems.map(x =>
        NumberUtil.ToNumber(x.LineItemTaxableAmount ?? (taxesPerLine ? x.Price : (x.IsTaxable || x.TaxId ? x.Price : 0)))));

      invoice.CalculatedTax += sum((invoice.AdditionalLineItems || []).map(l => {

        if (!taxesPerLine) {
          return 0;
        }

        const taxable = l.LineItemTaxableAmount ?? l.Price;
        const lineTax = !l.Tax ? l.LineItemTaxAmount : NumberUtil.CalculatePercentAmount(taxable, l.Tax?.Percent || 0, true);
        l.LineItemTaxAmount = lineTax;
        return lineTax;
      }));
    }

    if (invoice.EstimatedLineItems?.length) {
      invoice.EstimatedLineItemsSubTotal += NumberUtil.ToNumber(_.sum(invoice.EstimatedLineItems.map(x => NumberUtil.ToNumber(x.InvoicedPrice))), 2);
      invoice.SubTotal += invoice.EstimatedLineItemsSubTotal;
      invoice.TaxableSubTotal += _.sum(invoice.EstimatedLineItems.map(x => NumberUtil.ToNumber(x.LineItemTaxableAmt || (taxesPerLine ? x.InvoicedPrice : 0))));
    }

    if (taxesPerLine && invoice.EstimatedLineItems?.length) {
      invoice.CalculatedTax += sum(invoice.EstimatedLineItems.filter(e => e.CostlineTax && e.InvoicedPrice).map(e => NumberUtil.ToNumber((e.CostlineTax / e.CostlineAmt) * e.InvoicedPrice)));
    } else {
      if (invoice.Tax) {
        invoice.CalculatedTax = NumberUtil.CalculatePercentAmount(invoice.Tax.Percent, invoice.TaxableSubTotal, true);
      }
    }


    if (invoice.EstimateEntity) {
      invoice.OtherInvoicedAmt = this.getTotalOtherLineItemsInvoicedPrice(invoice.EstimatedLineItems);
      invoice.OtherInvoicedAmtPercent = NumberUtil.CalculateTotalPercent(invoice.EstimateEntity.EstimateValues?.TotalWithoutTax, invoice.OtherInvoicedAmt);
    }

    invoice.OtherAdditionalAmt = this.getTotalOtherAdditionalAmt(invoice);

    let disObj = this.discountService.CalculateDiscount(invoice.SubTotal, invoice.TaxableSubTotal, invoice.CalculatedTax, invoice.Tax?.Percent, invoice.InvoiceDiscounts);
    if (disObj) {
      invoice.TotalDiscount = disObj?.DiscountValue || 0;
      invoice.TaxableSubTotal = disObj?.TaxableTotal || 0;
      invoice.CalculatedTax = disObj?.TotalTax || 0;
      invoice.TaxableGapDueToDiscount = disObj?.TaxableGapDueToDiscount || 0;
    }

    invoice.Total = decimal2((decimal2(invoice.SubTotal) + invoice.CalculatedTax + (invoice.TaxRoundingAdjustment || 0)) - (invoice.TotalDiscount || 0));
    invoice.PaymentToDate = this.getTotalOfAllPayment(invoice.Payments);
    invoice.RemainingBalance = decimal2(decimal2(invoice.Total) - invoice.PaymentToDate);
    // invoice.RemainingBalance = invoice.RemainingBalance > 0 ? invoice.RemainingBalance : 0; // as values can be negative now

    invoice.TotalInvoicedAmtWithoutTax = (invoice.OtherInvoicedAmt || 0) + invoice.SubTotal;
    invoice.TotalInvoicedAmtWithTax = (invoice.OtherInvoicedAmt || 0) + invoice.Total;
    invoice.EstimatedLineItemsTotal = (invoice.OtherInvoicedAmt || 0) + invoice.EstimatedLineItemsSubTotal;

    if (invoice.EstimateEntity) {
      invoice.TotalPercentCompleteWithoutTax = this.getTotalPercentComplete(invoice.TotalInvoicedAmtWithoutTax, invoice.EstimateEntity.EstimateValues?.TotalWithoutTax);
      invoice.EstimatedLineItemsTotalPercent = this.getTotalPercentComplete(invoice.EstimatedLineItemsTotal, invoice.EstimateEntity.EstimateValues?.TotalWithoutTax);
    }
  }

  renderInvoiceDiscounts(invoice: InvoiceEntityDTO) {
    if (invoice.InvoiceDiscounts?.length > 0) {
      invoice.InvoiceDiscounts = this.discountService.MapDiscounts(invoice.InvoiceDiscounts);
    }
    return invoice;
  }

  getTotalPercentComplete(invoicedPrice: number, totalEstimatedPrice: number) {
    let totalPercentComplete = NumberUtil.CalculateTotalPercent(totalEstimatedPrice, invoicedPrice);
    return NumberUtil.ToNumber(totalPercentComplete);
  }

  getTotalOtherAdditionalAmt(invoice: InvoiceEntityDTO) {
    return _.sum((invoice.OtherInvoices || []).flatMap(x => (x.AdditionalLineItems || [])).flatMap(a => NumberUtil.ToNumber(a.Price)));
  }

  getTotalOtherLineItemsInvoicedPrice(lineItems: InvoiceEstimatedLineItemsDTO[]) {
    return _.sum((lineItems || []).map(x => NumberUtil.ToNumber(x.OtherInvoiced)) || 0);
  }


  async estimatedLineItemsMapping(estimate: EstimateEntityDTO, invoice: InvoiceEntityDTO, costlineTaxEnabled: boolean, checkSelection: boolean = false): Promise<InvoiceEstimatedLineItemsDTO[]> {
    let estimateLineItems = [] as InvoiceEstimatedLineItemsDTO[];
    if (!estimate) {
      return estimateLineItems;
    }

    if (estimate?.EstimateDetails) {
      let estimateLineItemsTemp = [] as InvoiceEstimatedLineItemsDTO[];
      for (const detail of estimate.EstimateDetails) {
        let detailsName = detail.Name + " " + detail.EstimateNumber;
        detail.EstimateDetailsSections = orderBy((detail.EstimateDetailsSections || []), c => c.SectionOrder, 'asc');
        for (const section of detail.EstimateDetailsSections) {
          estimateLineItemsTemp = [] as InvoiceEstimatedLineItemsDTO[];
          if (estimate.IsItemized) {
            await this.estimateCostlinesMapping(detailsName, detail.Id, detail.CreatedDate, [section], detail.EstimateType, estimateLineItemsTemp, invoice, costlineTaxEnabled, false, checkSelection);
          } else {
            await this.estimateSectionsMapping(detailsName, detail.Id, detail.CreatedDate, [section], detail.EstimateType, estimateLineItemsTemp, invoice, costlineTaxEnabled, checkSelection);
          }
          let lines = cloneDeep(estimateLineItemsTemp);

          // exclude items from lines by checking in invoice.AdditionalLineItems, if any item is present in 
          // invoice.AdditionalLineItems that have matching MovedFrom property.
          lines = lines.filter(f => !(invoice.AdditionalLineItems || []).some(h => h.MovedFrom === f.Id));

          if (lines?.length > 0) {
            estimateLineItemsTemp = [];
            let groupedBySectionWiseOrder = _.groupBy(_.orderBy(lines, f => f.SectionWiseOrder), f => f.SectionWiseOrder);
            Object.values(groupedBySectionWiseOrder).map(f => {
              if (f.length == 1)
                estimateLineItems.push(...f);
              else {
                let innerList = _.orderBy(f, g => g.Order);
                estimateLineItems.push(...innerList);
              }
            })
          }
        }
      }
    }

    return estimateLineItems;
  }

  //estimate costline wise mapping
  private async estimateCostlinesMapping(estimateDetailName: string, estimateDetailId: string, estimateDetailCreatedDate: Date, estimateSections: EstimateDetailsSectionEntityDTO[], estimateType: EstimateDetailTypeEnum, estimateLineItems: InvoiceEstimatedLineItemsDTO[], invoice: InvoiceEntityDTO, costlineTaxEnabled: boolean, isSubSection = false, checkSelection = false) {
    for await (const section of estimateSections) {

      //Removed section from estimate details
      if (section.IsDisplayOnly) {
        await this.estimateSectionsMapping(estimateDetailName, estimateDetailId, estimateDetailCreatedDate, [section], estimateType, estimateLineItems, invoice, costlineTaxEnabled)
      } else {

        //sort subsection costline with order & sort section costline with sectionwise order
        section.EstimateCostLineItems = orderBy((section.EstimateCostLineItems || []), c => c.SectionWiseOrder, 'asc');

        if (checkSelection) {
          section.EstimateCostLineItems = section.IsSelectionEnabled ? section.EstimateCostLineItems.filter(f => f.IsSelectedForSelection) : section.EstimateCostLineItems;
        }

        for await (const x of section.EstimateCostLineItems) {

          let invoicedItem = (invoice.EstimatedLineItems || []).filter(c => c.CostLineId == x.Id)[0];
          const costlineName = textToHtml(x.Name);
          const sectionCostlineNameDesc = (costlineName.length ? `<p>${costlineName}</p>` + '<p>&nbsp;</p>' : '') + ((x.Description || '').length ? x.Description : '')
          const createObject = await this.createEstimateLineItemObject(invoicedItem?.Id, x.Id, (invoicedItem?.Name || costlineName), x.Props?.icon, (invoicedItem?.Description || x.Description), (invoicedItem?.InvoicedPrice || 0), estimateType, x.Price, EstimateDetailSectionCostlineTypeEnum.Costline, estimateDetailName, estimateDetailId, estimateDetailCreatedDate, x.IsTaxable, x.CalculatedTax, x.taxableAmt, section.IsDisplayOnly, !!section.RemovedBySectionId?.length, (section.ModifiedFromSectionId?.length || 0) > 0, invoice, sectionCostlineNameDesc, costlineTaxEnabled, x.Order, isSubSection ? section.SectionWiseOrder : x.SectionWiseOrder);
          estimateLineItems.push(createObject);
        }

        if (section.hasOwnProperty("SubEstimateDetails") && section.SubEstimateDetails?.filter(x => x?.EstimateCostLineItems?.length)?.length) {
          if (checkSelection) {
            section.SubEstimateDetails = section.IsSelectionEnabled ? section.SubEstimateDetails.filter(f => f.IsSelectedForSelection) : section.SubEstimateDetails;
          }
          await this.estimateCostlinesMapping(estimateDetailName, estimateDetailId, estimateDetailCreatedDate, section.SubEstimateDetails, estimateType, estimateLineItems, invoice, costlineTaxEnabled, true);
        }
      }
    }
  }

  private async estimateSectionsMapping(estimateDetailName: string, estimateDetailId: string, estimateDetailCreatedDate: Date, estimateSections: EstimateDetailsSectionEntityDTO[], estimateType: EstimateDetailTypeEnum, estimateLineItems: InvoiceEstimatedLineItemsDTO[], invoice: InvoiceEntityDTO, costlineTaxEnabled: boolean, checkSelection: boolean = false) {
    for await (const section of estimateSections) {
      let invoicedItem = (invoice.EstimatedLineItems || []).filter(c => c.SectionId == section.Id)[0];

      const sectionNameHtml = textToHtml(section.Name);
      let sectionName = (invoicedItem?.Name || sectionNameHtml);
      let sectionCostlineNameDesc = sectionNameHtml.length ? ((`<p>${sectionNameHtml}</p>`) + '<p>&nbsp;</p>') : '';
      let sectionAllItems: any = [];

      let displayList = this.estimateService.renderDisplaySection(section, [], false, checkSelection);
      section.EstimateCostLineItems = displayList;

      if (displayList?.length) {
        displayList.forEach(item => {
          if (item.renderedType == EstimateSectionDisplayRenderType.costline)
            sectionAllItems.push(item)
          else if (item.renderedType == EstimateSectionDisplayRenderType.subsection) {
            sectionAllItems.push(item);
            if (item.EstimateCostLineItems?.length) {
              let subEstimateCostlineItems = item.EstimateCostLineItems;
              if (subEstimateCostlineItems?.length) sectionAllItems.push(...subEstimateCostlineItems);
            }
          }
        });

        for (const costline of sectionAllItems) {
          if (costline.IsViewable) {
            sectionCostlineNameDesc = (sectionCostlineNameDesc || '') + '' + ((costline?.Name || '').length ? (`<p>${textToHtml(costline.Name)}</p>`) + '<p>&nbsp;</p>' : '') + '' + ((costline?.Description || '').length ? ((costline.Description || '') + '<p>&nbsp;</p>') : '');
          }
        }
      }

      if ((sectionName?.length || 0) == 0) {
        sectionName = sectionCostlineNameDesc || '';
      }

      let createObject = await this.createEstimateLineItemObject(invoicedItem?.Id, section.Id, sectionName, '', (invoicedItem?.Description || ''), (invoicedItem?.InvoicedPrice || 0), estimateType, this.estimateService.calculateEstimateSectionTotal(section), EstimateDetailSectionCostlineTypeEnum.Section, estimateDetailName, estimateDetailId, estimateDetailCreatedDate, section.IsTaxable, section.CalculatedTax, section.TaxableTotal, section.IsDisplayOnly, !!section.RemovedBySectionId?.length, (section.ModifiedFromSectionId?.length || 0) > 0, invoice, sectionCostlineNameDesc, costlineTaxEnabled, section.Order, section.SectionOrder);
      estimateLineItems.push(createObject);
    }
  }

  async createEstimateLineItemObject(invoicedItemId: string, id: string, name: string, icon: string, description: string, invoicedPrice: number, type: EstimateDetailTypeEnum, estimatedPrice: number, detailType: EstimateDetailSectionCostlineTypeEnum, estimateDetailName: string, estimateDetailId: string, estimateDetailCreatedDate: Date, isTaxable: boolean, includedTax: number, taxablePrice: number, isDisplayOnly: boolean, isRemovedFromChangeOrder: boolean, isModified: boolean, invoice: InvoiceEntityDTO, sectionCostlineNameDesc: string, costlineTaxEnabled: boolean, order: number, sectionWiseOrder: number): Promise<InvoiceEstimatedLineItemsDTO> {
    let data = new InvoiceEstimatedLineItemsDTO();
    data.Id = invoicedItemId;
    data.Name = name || description;
    data.Description = description;
    data.CostlineAmt = estimatedPrice;
    data.InvoicedPrice = invoicedPrice;
    data.InvoicedPricePercent = data.CostlineAmt == 0 ? 0 : NumberUtil.CalculateTotalPercent(data.CostlineAmt, data.InvoicedPrice);
    data.EstimateDetailName = estimateDetailName;
    data.EstimateDetailCreatedDate = estimateDetailCreatedDate;
    data.EstimateDetailId = estimateDetailId;
    data.EstimateType = type;
    data.SectionId = detailType == EstimateDetailSectionCostlineTypeEnum.Section ? id : '';
    data.CostLineId = detailType == EstimateDetailSectionCostlineTypeEnum.Costline ? id : '';
    data.IsTaxable = isTaxable;
    data.CostlineTax = costlineTaxEnabled ? includedTax : NumberUtil.CalculatePercentAmount(taxablePrice, invoice.Tax?.Percent || 0, true);
    data.Icon = icon || 'i-coin_dollar';
    data.IsDisplayOnly = isDisplayOnly;
    data.IsRemovedFromChangeOrder = isRemovedFromChangeOrder;
    data.CostlineTaxableAmt = taxablePrice;
    data.LineItemTaxableAmt = this.getLineItemTaxable(taxablePrice, invoicedPrice, data.CostlineAmt);
    data.LineItemTax = NumberUtil.CalculatePercentAmount(data.LineItemTaxableAmt, invoice.Tax?.Percent || 0, true);
    data.OtherInvoiced = invoice.OtherInvoices?.length ? _.sum(invoice.OtherInvoices.filter(d => d.Status == InvoiceStatusEnum.Paid).flatMap(x => x.EstimatedLineItems).filter(e => (detailType == EstimateDetailSectionCostlineTypeEnum.Costline ? e.CostLineId == id : e.SectionId == id)).map(x => (x.InvoicedPrice || 0))) : 0;
    data.OtherInvoiced = invoice.OtherInvoices?.length ? _.sum(invoice.OtherInvoices.flatMap(x => x.EstimatedLineItems).filter(e => (detailType == EstimateDetailSectionCostlineTypeEnum.Costline ? e.CostLineId == id : e.SectionId == id)).map(x => (x.InvoicedPrice || 0))) : 0;
    data.OtherInvoicedPercent = data.CostlineAmt == 0 ? 0 : NumberUtil.CalculateTotalPercent(data.CostlineAmt, data.OtherInvoiced);
    data.TotalInvoiced = (data.InvoicedPrice || 0) + data.OtherInvoiced;
    data.TotalInvoicedPercent = data.CostlineAmt == 0 ? 0 : NumberUtil.CalculateTotalPercent(data.CostlineAmt, data.TotalInvoiced);
    data.TotalPercentComplete = data.CostlineAmt == 0 ? 0 : this.getTotalPercentComplete(data.TotalInvoiced, data.CostlineAmt);
    data.Order = order;
    data.SectionCostlineNameDesc = sectionCostlineNameDesc;
    data.SectionWiseOrder = sectionWiseOrder;
    return data;
  }

  getLineItemTaxable(costlineTaxableAmt: number, invoicedPrice: number, costlineCost: number) {
    return (costlineTaxableAmt || 0) * ((invoicedPrice || 0) / (costlineCost || 0));
  }

  groupChangeOrderLineItems(changeOrderLineItems: InvoiceEstimatedLineItemsDTO[]) {

    var sorted = _.sortBy((changeOrderLineItems || []), x => x.EstimateDetailCreatedDate, ['asc']);

    return _(sorted.filter(x => x.EstimateType == EstimateDetailTypeEnum.ChangeOrder))
      .groupBy(x => x.EstimateDetailName)
      .map((value, key) => ({ Name: key, LineItems: value, isPrice: value.some(y => NumberUtil.ToNumber(y.InvoicedPrice) !== 0) }))
      .value();
  }


  async getFormattedInvoices(invoiceEntities: InvoiceEntityDTO[], estimate: EstimateEntityDTO, costlineTaxEnabled: boolean): Promise<InvoiceEntityDTO[]> {
    let formattedInvoices = [] as InvoiceEntityDTO[];

    if (invoiceEntities?.length) {

      for (let invoice of invoiceEntities) {
        let formattedInvoice = { ...invoice } as any as InvoiceEntityDTO;

        if (formattedInvoice.EstimateId) {
          formattedInvoice.EstimatedLineItems = await this.estimatedLineItemsMapping(estimate, formattedInvoice, costlineTaxEnabled, true);
        }

        formattedInvoice.TotalPayment = this.getTotalOfAllPayment(invoice.Payments);
        this.calculateInvoiceTotalValues(formattedInvoice);
        formattedInvoice.InvoicePercentage = formattedInvoice?.PaymentToDate > 0 ? NumberUtil.CalculateTotalPercent(formattedInvoice.Total, formattedInvoice.PaymentToDate) : 0;
        formattedInvoice.Status = this.getInvoiceStatus(formattedInvoice);
        formattedInvoices.push(formattedInvoice);
      }
    }
    return formattedInvoices;
  }

  getInvoiceStatus(invoice: InvoiceEntityDTO): InvoiceStatusEnum {
    if (invoice.ForceComplete) return InvoiceStatusEnum.Paid;

    const today = MomentHelper.GetDate();
    const { Payments, RemainingBalance, InvoiceDueDate, Status } = invoice;

    if (ValidInvoicePaymentsExists(Payments) && IsInvoicePublished(invoice.Status)) {
      if (RemainingBalance > 0) {
        return InvoiceDueDate && MomentHelper.IsBefore(InvoiceDueDate, today)
          ? InvoiceStatusEnum.Overdue
          : InvoiceStatusEnum.Partial;
      }
      return InvoiceStatusEnum.Paid;
    }

    if (Status === InvoiceStatusEnum.PendingPayment && InvoiceDueDate) {
      return MomentHelper.IsBefore(InvoiceDueDate, today)
        ? InvoiceStatusEnum.Overdue
        : InvoiceStatusEnum.PendingPayment;
    }

    if (Status === InvoiceStatusEnum.Overdue && InvoiceDueDate) {
      return MomentHelper.IsSameOrAfter(InvoiceDueDate, today)
        ? InvoiceStatusEnum.PendingPayment
        : InvoiceStatusEnum.Overdue;
    }

    return Status;
  }

  getTotalOfAllPayment(payments: IPayment[]) {
    return payments?.length ? _.sum(payments.map(x => (x.Status == PaymentStatusTypes.Completed || x.Status == PaymentStatusTypes.FullyRefunded || x.Status == PaymentStatusTypes.PartiallyRefunded) ? (x.Amount || 0) : 0)) : 0;
  }

  getTotalLineItemsInvoicedPrice(lineItems: InvoiceEstimatedLineItemsDTO[], withTax: boolean = false) {
    return _.sum((lineItems || []).map(x => NumberUtil.ToNumber(x.InvoicedPrice) + (withTax ? NumberUtil.ToNumber(x.LineItemTax) : 0)) || []);
  }

  invoiceProjectFinancials(invoice: InvoiceEntityDTO) {
    // Initialize totals
    invoice.OtherInvoicesTotal = 0;
    invoice.TotalEstimated = 0;
    invoice.ProjectDiscounts = 0;

    // Filter and check invoices once
    const { invoicesBeforeCurrentInvoice, hasInvoiceAfterCurrent } = this.categorizeInvoices(
      invoice.OtherInvoices || [],
      invoice.InvoiceDate,
      invoice.Id
    );

    // Calculate for current invoice
    this.calculateInvoiceFinancials(
      invoice,
      invoice,
      true
    );

    // Calculate for previous invoices
    if (invoicesBeforeCurrentInvoice.length > 0) {
      for (const inv of invoicesBeforeCurrentInvoice) {
        this.calculateInvoiceFinancials(
          invoice,
          inv,
          false
        );
      }
    }

    // Calculate final balance
    invoice.EstimateProjectBalance = decimal2(
      (invoice.TotalEstimated ?? 0) -
      (invoice.ProjectDiscounts ?? 0) -
      (invoice.OtherInvoicesTotal ?? 0));

    // The invoice editor clamps this balance at zero; the shared document must agree.
    if (invoice.EstimateProjectBalance < 0) {
      invoice.EstimateProjectBalance = 0;
    }
  }

  /**
   * Categorizes invoices relative to the current invoice date. Unpublished ones are kept here;
   * their lines are filtered out later.
   */
  private categorizeInvoices(
    otherInvoices: InvoiceEntityDTO[],
    currentInvoiceDate: Date,
    currentInvoiceId: string
  ): { invoicesBeforeCurrentInvoice: InvoiceEntityDTO[]; hasInvoiceAfterCurrent: boolean } {
    const invoicesBeforeCurrentInvoice: InvoiceEntityDTO[] = [];
    let hasInvoiceAfterCurrent = false;

    for (const inv of otherInvoices) {
      if (inv.Id === currentInvoiceId) {
        continue;
      }

      if (MomentHelper.IsSameOrBeforeDate(inv.InvoiceDate, currentInvoiceDate)) {
        invoicesBeforeCurrentInvoice.push(inv);
      } else {
        hasInvoiceAfterCurrent = true;
      }
    }

    return { invoicesBeforeCurrentInvoice, hasInvoiceAfterCurrent };
  }

  /**
   * Handles percentage and fixed discounts. A pre-tax discount also returns the tax that would
   * have applied to the discounted amount.
   */
  private calculateDiscountTotal(
    discounts: any[],
    total: number,
    taxPercent: number = 0
  ): number {
    // For percentage-based discounts, the calculation using `total` (which includes tax)
    // already accounts for the tax impact, so no additional adjustment is needed
    const percentageDiscounts = sum(
      discounts
        .filter(d => d.IsPercentage)
        .map(d => (d.ApplyBeforeTax ? (total * ((d.Value || 0) / 100)) : d.DiscountedValue) || 0)
    );

    // For fixed (dollar) pre-tax discounts, we need to add the tax that would have been
    // applied to the discounted amount since DiscountedValue is just the flat dollar amount
    const fixedDiscounts = sum(
      discounts
        .filter(d => !d.IsPercentage)
        .map(d => {
          const discountValue = d.DiscountedValue || 0;
          const taxOnDiscount = (d.ApplyBeforeTax && taxPercent > 0) ? (discountValue * (taxPercent / 100)) : 0;
          return discountValue + taxOnDiscount;
        })
    );

    return percentageDiscounts + fixedDiscounts;
  }

  private processUnpublishedEstimateDetail(
    targetInvoice: InvoiceEntityDTO,
    estimateDetail: EstimateDetailsEntityDTO
  ): { totalEstimated: number; projectDiscounts: number } {
    let totalEstimated = 0;
    let projectDiscounts = 0;

    const linesToInclude = this.getInvoiceLinesToInclude(targetInvoice, [estimateDetail]);
    const total = this.getTotalLineItemsInvoicedPrice(linesToInclude, true) || 0;
    totalEstimated += total;

    if (targetInvoice.InvoiceDiscounts.length > 0 && estimateDetail.EstimateDiscounts?.length > 0) {
      const discounts = targetInvoice.InvoiceDiscounts.filter(f => f.EstimateDetailId === estimateDetail.Id);
      const taxPercent = targetInvoice.Tax?.Percent || 0;
      projectDiscounts += this.calculateDiscountTotal(discounts, total, taxPercent);
    }

    return { totalEstimated, projectDiscounts };
  }

  /** Skips any discount already counted on the estimate. */
  private addInvoiceDiscounts(
    sourceInvoice: InvoiceEntityDTO,
    estimateDetailsIds: string[],
    discountSource: any[]
  ): number {
    const discounts = discountSource.filter(
      f => !f.IsDiscountFromEstimate || !estimateDetailsIds.includes(f.EstimateDetailId)
    );

    if (discounts.length === 0) {
      return 0;
    }

    const taxPercent = sourceInvoice.Tax?.Percent || 0;
    return sum(discounts.map(d =>
      ((d.ApplyBeforeTax && taxPercent > 0 ? (d.DiscountedValue * (taxPercent / 100)) : 0) + d.DiscountedValue) || 0
    ));
  }

  /**
   * A Closed invoice is finalized and can never be invoiced again, so it counts like a published
   * one. Dropping it would show the customer the full project total as still due.
   */
  private isInvoiceFinanciallyCounted(status: InvoiceStatusEnum): boolean {
    return IsInvoicePublished(status) || status === InvoiceStatusEnum.Closed;
  }

  /**
   * A published or closed invoice contributes all of its lines; an unpublished one contributes
   * only the lines belonging to published estimate details.
   */
  private calculateInvoiceFinancials(
    targetInvoice: InvoiceEntityDTO,
    sourceInvoice: InvoiceEntityDTO,
    isCurrentInvoice: boolean
  ): void {
    // Cache commonly used values (Closed invoices count the same as published)
    const isSourceInvoicePublished = this.isInvoiceFinanciallyCounted(sourceInvoice.Status);
    const isTargetInvoicePublished = this.isInvoiceFinanciallyCounted(targetInvoice.Status);
    const estimateDetailsIds = (targetInvoice.EstimateEntity?.EstimateDetails || []).map(d => d.Id);

    // Add invoice total (current invoice or published source invoice)
    if (isCurrentInvoice || isSourceInvoicePublished) {
      targetInvoice.OtherInvoicesTotal += (sourceInvoice.Total || 0);
    }

    // Calculate estimated total
    if (isCurrentInvoice) {
      this.processCurrentInvoiceEstimates(
        targetInvoice,
        isTargetInvoicePublished,
        estimateDetailsIds,
        sourceInvoice
      );
    } else {
      this.processPreviousInvoiceEstimates(
        targetInvoice,
        sourceInvoice,
        isSourceInvoicePublished,
        estimateDetailsIds
      );
    }
  }

  private processCurrentInvoiceEstimates(
    targetInvoice: InvoiceEntityDTO,
    isTargetInvoicePublished: boolean,
    estimateDetailsIds: string[],
    sourceInvoice: InvoiceEntityDTO
  ): void {
    const estimateDetails = targetInvoice.EstimateEntity?.EstimateDetails || [];

    estimateDetails.forEach(d => {
      const isAfterOrSameDate = MomentHelper.IsSameOrAfterDate(targetInvoice.InvoiceDate, d.EstimateDate);

      if (this.estimateService.IsPublished(d.Status) && isAfterOrSameDate) {
        // Published estimate detail
        const total = this.estimateService.calculateCrossedOutTotal(d, estimateTaxMode(targetInvoice.EstimateEntity)) || 0;

        if (d.EstimateDiscounts?.length > 0) {
          const taxPercent = d.TaxEntity?.Percent || targetInvoice.Tax?.Percent || 0;
          targetInvoice.ProjectDiscounts += this.calculateDiscountTotal(d.EstimateDiscounts, total, taxPercent);
        }

        targetInvoice.TotalEstimated += total;
      } else if (isTargetInvoicePublished) {
        // Unpublished estimate detail with published target invoice
        const result = this.processUnpublishedEstimateDetail(targetInvoice, d);
        targetInvoice.TotalEstimated += result.totalEstimated;
        targetInvoice.ProjectDiscounts += result.projectDiscounts;
      }
    });

    // Add additional line items for published invoices
      if (isTargetInvoicePublished && targetInvoice.AdditionalLineItems?.length > 0) {
      const additionalTotal = this.calculateTaxableAdditionalTotal(targetInvoice.AdditionalLineItems, targetInvoice.Tax);
      targetInvoice.TotalEstimated += additionalTotal;
    }

    // Add invoice discounts (excluding duplicates from estimate)
    if (targetInvoice.InvoiceDiscounts?.length > 0) {
      targetInvoice.ProjectDiscounts += this.addInvoiceDiscounts(
        sourceInvoice,
        estimateDetailsIds,
        targetInvoice.InvoiceDiscounts
      );
    }
  }

  private processPreviousInvoiceEstimates(
    targetInvoice: InvoiceEntityDTO,
    sourceInvoice: InvoiceEntityDTO,
    isSourceInvoicePublished: boolean,
    estimateDetailsIds: string[]
  ): void {
    const estimateDetails = targetInvoice.EstimateEntity?.EstimateDetails || [];
    const unpublishedEstimateDetails = estimateDetails.filter(
      f => !this.estimateService.IsPublished(f.Status)
    );

    // Calculate totals from invoice lines
    const linesToInclude = this.getInvoiceLinesToInclude(sourceInvoice, unpublishedEstimateDetails);
    targetInvoice.TotalEstimated += this.getTotalLineItemsInvoicedPrice(linesToInclude, true) || 0;

    if (isSourceInvoicePublished) {
      // Add additional line items
   if (sourceInvoice.AdditionalLineItems?.length > 0) {
        const additionalTotal = this.calculateTaxableAdditionalTotal(sourceInvoice.AdditionalLineItems, sourceInvoice.Tax);
        targetInvoice.TotalEstimated += additionalTotal;
      }

      // Handle tax differences for published estimate details
      const publishedEstimateDetails = estimateDetails.filter(f => this.estimateService.IsPublished(f.Status));
      publishedEstimateDetails.forEach(d => {
        if (MomentHelper.IsBeforeDate(new Date(sourceInvoice.InvoiceDate), new Date(d.EstimateDate))
          && d.TaxId !== sourceInvoice.TaxId) {
          const linesToInclude = this.getInvoiceLinesToInclude(sourceInvoice, [d]);
          const total = this.getTotalLineItemsInvoicedPrice(linesToInclude) || 0;
          const taxDiff = NumberUtil.CalculatePercentAmount(
            total,
            (d.TaxEntity?.Percent || 0) - (sourceInvoice.Tax?.Percent || 0),
            true
          );
          targetInvoice.TotalEstimated += Math.abs(taxDiff);
        }
      });

      // Add invoice discounts
      if (sourceInvoice.InvoiceDiscounts?.length > 0) {
        targetInvoice.ProjectDiscounts += this.addInvoiceDiscounts(
          sourceInvoice,
          estimateDetailsIds,
          sourceInvoice.InvoiceDiscounts
        );
      }
    }
  }
  private calculateTaxableAdditionalTotal(additionalLineItems: any[], tax: any): number {
    if (!additionalLineItems?.length) return 0;
    let additionalTaxableTotal = sum(additionalLineItems.filter(d => d.IsTaxable === true).map(d => d.Price || 0));
    let additionalTotal = sum(additionalLineItems.map(d => d.Price || 0));
    if (tax?.Percent > 0 && additionalTaxableTotal > 0) {
      additionalTotal += additionalTaxableTotal / 100 * tax.Percent;
    }
    return additionalTotal;
  }
  

  private getInvoiceLinesToInclude(
    invoice: InvoiceEntityDTO,
    estimateDetails: EstimateDetailsEntityDTO[]
  ): InvoiceEstimatedLineItemsDTO[] {
    const invoiceLines = invoice.EstimatedLineItems || [];

    if (!invoiceLines.length) {
      return [];
    }

    const publishedEstimateDetailIds = new Set(
      estimateDetails.map(detail => detail.Id)
    );

    if (publishedEstimateDetailIds.size === 0) {
      return [];
    }

    return invoiceLines.filter(line =>
      line.EstimateDetailId && publishedEstimateDetailIds.has(line.EstimateDetailId)
    );
  }
}

